import React, {
	useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
	SETTINGS_TABS, ROOM_STATES, AppSettings, DefaultMeetingImage,
} from '../../stores/reducers/settings';
import PreviewOnLayouts from './settings/preview-on-layouts';
import Spacing from './settings/layouts/spacing';
import Background from './settings/background';
import Titles from './settings/titles';
import WallpaperBG from './settings/wallpaper-bg';
import * as SettingsActions from '../../stores/actions/settings';
import * as VideoActions from '../../stores/actions/video';
import Socket from '../../connection/socket';
import * as ShotsActions from '../../stores/actions/shots';
import { GreenroomState } from '../../stores/types/types';
import useShots from '../../contexts/shots/provider';
import SlidingMenu, { SlidingMenuOption } from '../general-components/sliding-menu';
import changeShotLayout from '../../utils/changeShotLayout';
import VideoBorder from './settings/layouts/video-border';
import VideoRadius from './settings/layouts/video-radius';
import { FIXED_LAYOUTS, PLAIN_LAYOUTS, getVisiblePresentersFromLayout } from '../../utils/video-layout';
import { validateShotData } from '../../connection/utils';
import { PutCanvasStyle } from '../../connection/shots';
import FixedLayoutMsg from './settings/layouts/fixed-plain-message';

interface Props {
	socket: Socket | null;
}

const menuOptions: SlidingMenuOption[] = [
	{ label: 'Layouts', data: { value: SETTINGS_TABS.LAYOUTS } },
	{ label: 'Titles', data: { value: SETTINGS_TABS.TITLES } },
	{ label: 'Canvas', data: { value: SETTINGS_TABS.CANVAS } },
];

export default function Settings(props: Props) {
	const { socket } = props;
	const dispatch = useDispatch();

	const useModeratedQuestions = useSelector(
		(state: GreenroomState) => state.SettingsReducer.useModeratedQuestions,
	);
	const settingsTab = useSelector((state: GreenroomState) => state.SettingsReducer.settingsTab);
	const overlayVisible = useSelector(
		(state: GreenroomState) => state.SettingsReducer.overlayVisible,
	);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const canvasStyle = useSelector((state: GreenroomState) => state.ShotsReducer.canvasStyle);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);

	const {
		handleSelectShot,
		currentShot,
	} = useShots();

	const { forcedLayout, shotFeeds } = currentShot.shot_data;

	const prevModeratedQuestions = useRef(useModeratedQuestions);
	const [imageQueue, setImageQueue] = useState<DefaultMeetingImage | null>(null);

	const [queuedLayout, setQueuedLayout] = useState(forcedLayout);
	const [queuedStyle, setQueuedStyle] = useState(canvasStyle);

	useEffect(() => setQueuedLayout(forcedLayout), [forcedLayout, roomState]);
	useEffect(() => setQueuedStyle(canvasStyle), [canvasStyle, roomState]);

	const styleUpdated = useMemo(
		() => JSON.stringify(canvasStyle) !== JSON.stringify(queuedStyle),
		[canvasStyle, queuedStyle],
	);

	const layoutUpdated = useMemo(
		() => queuedLayout !== forcedLayout,
		[queuedLayout, forcedLayout],
	);

	const updated = useMemo(
		() => (styleUpdated || layoutUpdated),
		[styleUpdated, layoutUpdated],
	);

	const didEditTitles = (old: AppSettings, newStyle: AppSettings): boolean => {
		const appliedTitlesSettings = Object.keys(old).filter((key) => key.toLowerCase().indexOf('title') > -1);
		let hasBeenEdited = false;
		appliedTitlesSettings.forEach((propName) => {
			if (Object(old)[propName] !== Object(newStyle)[propName]) {
				hasBeenEdited = true;
			}
		});
		return hasBeenEdited;
	};

	const applySettingsChanges = () => {
		if (styleUpdated && token && meeting?.meeting) {
			PutCanvasStyle(token, meeting.meeting, queuedStyle);
		}

		const updatedShot = shots.find((shot) => shot.shot_id === currentShot.shot_id);
		if (layoutUpdated && token) {
			prevModeratedQuestions.current = useModeratedQuestions;
			if (updatedShot) {
				const itemCount = getVisiblePresentersFromLayout(queuedLayout);
				const editedShot = changeShotLayout(updatedShot, queuedLayout, itemCount);
				handleSelectShot(updatedShot.shot_id);
				if (editedShot) {
					dispatch(ShotsActions.editShot(token, editedShot));
				}
			}
		}

		if (didEditTitles(canvasStyle, queuedStyle)) {
			socket?.sendMessage('toggle-all-titles', [[0], updatedShot?.shot_id]);
		}

		if (imageQueue) {
			dispatch(SettingsActions.setDefaultMeetingImage(imageQueue));
			setImageQueue(null);
		}
	};

	const revertSettingsChanges = () => {
		setQueuedLayout(forcedLayout);
		setQueuedStyle(canvasStyle);
		setImageQueue(null);
	};

	const settingsEdit = useCallback((newQueue: AppSettings) => {
		setQueuedStyle(newQueue);

		// showbuilder, edit shots right away - will fire update-shot-style socket/dispatch
		if (roomState === ROOM_STATES.EDITING && token && meeting?.meeting) {
			PutCanvasStyle(token, meeting.meeting, newQueue);
		}
	}, [token, meeting?.meeting, roomState]);

	async function updateLayout(layoutSize: number, layout: string) {
		setQueuedLayout(layout);
		if (roomState === ROOM_STATES.EDITING) {
			// edit shot right away when in showbuilder
			if (!token) return;
			const newShot = JSON.parse(JSON.stringify(currentShot));
			const editedShot = changeShotLayout(newShot, layout, layoutSize);
			if (editedShot?.shot_data) {
				editedShot.shot_data = await validateShotData(editedShot.shot_data, media);
			}
			if (editedShot) {
				dispatch(ShotsActions.editShot(token, editedShot));
			}
		}
	}

	function updateDefaultMeetingImage(wallpaperData: DefaultMeetingImage) {
		if (roomState === ROOM_STATES.EDITING) {
			setImageQueue(wallpaperData);
		} else {
			dispatch(SettingsActions.setDefaultMeetingImage(wallpaperData));
		}
	}

	const changeTab = (option: SlidingMenuOption) => {
		dispatch(SettingsActions.setSettingsTab(option.data.value));
	};

	const [isPlain, isFixed] = useMemo((): [boolean, boolean] => {
		const plain = PLAIN_LAYOUTS.some((l) => l === queuedLayout);
		const fixed = FIXED_LAYOUTS.some((l) => l === queuedLayout);

		return [plain, fixed];
	}, [queuedLayout]);

	return (
		<div className="settings">
			<SlidingMenu
				options={menuOptions}
				selectedOption={menuOptions
					.find((menuOption) => menuOption.data.value === settingsTab) ?? menuOptions[0]}
				callback={changeTab}
				className="padding-left-small position-relative margin-bottom-medium"
				fullWidthDivider={true}
			/>

			{settingsTab === SETTINGS_TABS.LAYOUTS && (
				<>
					<PreviewOnLayouts
						updateLayout={updateLayout}
						previewLayout={queuedLayout}
						shotFeedsLength={shotFeeds.length}
					/>
					<FixedLayoutMsg 
						previewLayout={queuedLayout}
					/>
					<div className={`${isPlain ? 'disabled' : ''}`}>
						<Spacing
							settingsForShotEdited={settingsEdit}
							previewLayout={queuedLayout}
							queue={queuedStyle}
							disabled={isFixed || isPlain}
						/>
						<div className="settings-container border-container">
							<label className="section-title margin-bottom-medium">
								<span className={classNames('title-text')}>Borders</span>
							</label>
							<VideoBorder
								settingsForShotEdited={settingsEdit}
								queue={queuedStyle}
							/>
							<hr />
							<VideoRadius
								settingsForShotEdited={settingsEdit}
								queue={queuedStyle}
							/>
						</div>
					</div>
				</>
			)}

			{
				settingsTab === SETTINGS_TABS.TITLES
				&& <Titles
					settingsForShotEdited={settingsEdit}
					appliedMatchesQueue={!updated}
					queue={queuedStyle}
				/>
			}

			{settingsTab === SETTINGS_TABS.CANVAS && (
				<>
					<WallpaperBG
						settingsForShotEdited={settingsEdit}
						updateDefaultMeetingImage={updateDefaultMeetingImage}
						appliedMatchesQueue={!updated}
						queue={queuedStyle}
					/>
					<Background
						settingsForShotEdited={settingsEdit}
						appliedMatchesQueue={!updated}
						queue={queuedStyle}
					/>
				</>
			)}

			{settingsTab !== SETTINGS_TABS.EVENT && roomState === ROOM_STATES.SHOWTIME && (
				<div className="apply-changes-buttons-container">
					<button
						disabled={!updated}
						className={classNames('reset-btn', { active: updated })}
						onClick={revertSettingsChanges}
					>
						<span>Reset</span>
					</button>
					<button
						type="button"
						disabled={!updated}
						className={classNames('apply-changes-btn', { active: updated })}
						onClick={applySettingsChanges}
					>
						<span className={updated ? 'enabled-text' : 'disabled-text'}>Save Changes</span>
					</button>
				</div>
			)}

			{overlayVisible
				&& <div className="settings-overlay" />}
		</div>
	);
}
